// @flow
import React from 'react';

import { withStyles } from '@material-ui/core/styles';
import Input from '@material-ui/core/Input';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import Counter from '../counter';
import stylesheet from './TextField.scss';

import * as tokens from 'shared/legacy/tokens/tokens.module';

type Props = {
  /** This is supplied internally from the HOC withStyles */
  classes: {
    focused: string,
    error: string,
    disabled: string,
    inputInput: string,
    inputError: string,
    inputRoot: string,
    inputInputType: string,
    inputUnderline: string,
    inputFormControl: string,
    helperTextRoot: string,
    inputMultiline: string,
  },
  error?: boolean,
  disabled?: boolean,
  required?: boolean,
  helperText?: string,
  multiline?: boolean,
  fullWidth?: boolean,
  rows?: number,
  placeholder?: string,
  rowsMax?: number,
  id: string,
  label: string,
  value?: string,
  maxLength?: number,
  errorMessage?: string,
  /** Available types 'email' | 'password' | 'text' by default */
  type?: 'email' | 'text' | 'password',
  handleChange: string => void,
};

// Need a rem converter for the px values
const styles = () => ({
  focused: {},
  error: {},
  disabled: {},
  inputInput: {
    padding: `${tokens.spacingTiny / 2}px ${tokens.spacingTiny}px`, // spacing override to match the design
    '&$error': {
      backgroundColor: tokens.colorRed500,
    },
  },
  inputError: {
    backgroundColor: tokens.colorRed100,
  },
  inputRoot: {
    fontFamily: '"Lato", sans-serif', // @TODO make this a font family token
    fontSize: `${tokens.fontSizeBodyCopy6}px`,
    lineHeight: `${tokens.lineHeightBodyCopy6}px`,
    color: tokens.colorNeutralDark900,
    backgroundColor: tokens.colorNeutralDark080,
  },
  inputInputType: {
    height: 'initial',
  },
  inputUnderline: {
    '&:after': {
      borderBottomColor: tokens.colorTeal200,
    },
    '&$inputError:after': {
      borderBottomColor: tokens.colorRed500,
    },
    '&:before': {
      borderBottomColor: tokens.colorNeutralDark400,
      borderWidth: '2px',
    },
  },
  inputFormControl: {
    'label + &': {
      marginTop: '0px',
    },
  },
  helperTextRoot: {
    fontFamily: '"Lato", sans-serif', // @TODO make this a font family token
    fontSize: `${tokens.fontSizeBodyCopy2}px`,
    lineHeight: `${tokens.lineHeightBodyCopy2}px`,
    marginTop: '0px',
    color: tokens.colorNeutralDark500,
    '&$error': {
      color: tokens.colorRed500,
    },
  },
  inputMultiline: {
    padding: `${tokens.spacingTiny / 2}px ${tokens.spacingTiny}px`, // spacing override to match the design
    fontSize: `${tokens.fontSizeBodyCopy6}px`,
  },
});

class TextField extends React.Component<Props> {
  render() {
    const {
      error,
      disabled,
      required,
      classes,
      id,
      helperText,
      label,
      value = '',
      type = 'text',
      multiline,
      fullWidth,
      rows,
      rowsMax,
      maxLength,
      errorMessage = 'Character limit exceeded, please reduce your response',
      placeholder,
      handleChange,
    } = this.props;

    return (
      <FormControl
        error={error}
        disabled={disabled}
        required={required}
        fullWidth={fullWidth}
      >
        <label className={stylesheet['textfield__label']} htmlFor={id}>
          {label}
        </label>
        <Input
          classes={{
            root: classes.inputRoot,
            underline: classes.inputUnderline,
            input: classes.inputInput,
            inputType: classes.inputInputType,
            error: classes.inputError,
            formControl: classes.inputFormControl,
            multiline: classes.inputMultiline,
          }}
          placeholder={placeholder}
          fullWidth={fullWidth}
          id={id}
          name={id}
          value={value}
          onChange={event => handleChange(event.target.value)}
          type={type}
          multiline={multiline}
          rows={rows}
          rowsMax={rowsMax}
        />
        {maxLength && (
          <Counter
            currentLength={value.length}
            maxLength={maxLength}
            errorMessage={errorMessage}
          />
        )}
        {helperText && (
          <FormHelperText
            id={`helpertext-${id}`}
            classes={{ error: classes.error, root: classes.helperTextRoot }}
          >
            {helperText}
          </FormHelperText>
        )}
      </FormControl>
    );
  }
}

export default withStyles(styles)(TextField);
